import { Grid, Stack } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Row from "../../../../../components/UI/Row";
import useDate from "../../../../../hooks/useDate";
import useFetch from "../../../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../../../services/tasySchedMenu.service";
import CalendarAddress from "../../../CalendarAddress";
import { ScheduleAppointmentService } from "../../../appointment.service";
import { TAppointment } from "../../schema";
import ConfirmDate from "../../Stepper/ConfirmDate";
import { Antecipate } from "./Antecipate/Antecipate";
import ConfirmPhoneNumber from "./ConfirmPhoneNumber/ConfirmPhoneNumber";
import TasyOrientations from "./TasyOrientations";

interface IConfirmProps {
  rule?: ITicketAutomationReceivedRule | null;
  useNewScheduleInsuranceRegister?: boolean;
}

const CONSULTATION_AUTOMATION_CODE = "20";
const EXAM_AUTOMATION_CODE = "21";

const getInsuranceIntegrationId = (insurance: any) =>
  insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

const getInsuranceCategoryCode = (insurance: any) =>
  insurance?.categoryIntegrationId ||
  insurance?.insuranceCategoryCode ||
  insurance?.category?.id;

const Confirm: React.FC<IConfirmProps> = ({
  rule,
  useNewScheduleInsuranceRegister = false,
}) => {
  const { getValues } = useFormContext<TAppointment>();
  const [price, setPrice] = useState<number | null>(null);

  const { t } = useTranslation();

  const { dayjs } = useDate();
  const { sendRequest: getSchedulePrice } = useFetch(
    ScheduleAppointmentService.getSchedulePriceTasy2
  );

  const values = getValues();
  const canSearchPrice = useMemo(
    () =>
      !!rule?.id &&
      [CONSULTATION_AUTOMATION_CODE, EXAM_AUTOMATION_CODE].includes(
        String(rule.ticketAutomationCode)
      ),
    [rule?.id, rule?.ticketAutomationCode]
  );

  const fetchPrice = useCallback(async () => {
    if (!canSearchPrice || !rule?.id) return;

    const currentValues = getValues() as any;
    const search = currentValues.search;
    const schedule = currentValues.schedule;
    const establishmentCode = currentValues.establishment?.integrationId;
    const insuranceCode = Number(
      getInsuranceIntegrationId(currentValues.insurance)
    );

    if (!insuranceCode || !schedule?.identifier) return;

    const { data, success } = await getSchedulePrice({
      ticketAutomationReceivedRuleId: rule.id,
      tasyScheduleTypeCode: Number(search.type),
      insuranceCode,
      insuranceCategoryCode: getInsuranceCategoryCode(currentValues.insurance),
      establishmentCode,
      medicalSpecialtyCode: search.speciality?.id
        ? Number(search.speciality.id)
        : undefined,
      calendarIdentifier: schedule.calendarIdentifier
        ? Number(schedule.calendarIdentifier)
        : undefined,
      procedureCode:
        schedule.procedureCode != null
          ? Number(schedule.procedureCode)
          : search.examGroup?.procedureCode != null
          ? Number(search.examGroup.procedureCode)
          : undefined,
      procedureOriginCode:
        schedule.procedureOriginCode != null
          ? Number(schedule.procedureOriginCode)
          : search.examGroup?.procedureOriginCode != null
          ? Number(search.examGroup.procedureOriginCode)
          : undefined,
      procedureInternalCode:
        schedule.procedureInternalCode != null
          ? Number(schedule.procedureInternalCode)
          : search.examGroup?.procedureInternalCode != null
          ? Number(search.examGroup.procedureInternalCode)
          : undefined,
    });

    setPrice(success && data?.price != null ? Number(data.price) : null);
  }, [canSearchPrice, getSchedulePrice, getValues, rule?.id]);

  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  const formattedPrice = useMemo(() => {
    if (price == null) return null;

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  }, [price]);

  return (
    <Grid container gap={3} flexDirection={"row"}>
      <Stack spacing={1}>
        <Row
          label={t(
            values.search?.type === "2"
              ? "page.schedules.newSchedule.search.procedure"
              : "page.schedules.newSchedule.confirm.descriptionSite",
          )}
          value={values.schedule.descriptionSite}
          compact
        />
        {values.search?.type !== "2" && values.search?.speciality?.name && (
          <Row
            label={t("page.schedules.newSchedule.search.speciality")}
            value={values.search.speciality.name}
            compact
          />
        )}
        <Row
          label={t("page.schedules.newSchedule.confirm.patient")}
          value={values.patient.name}
          compact
        />

        <Row
          label={t("page.schedules.newSchedule.confirm.insurance")}
          value={values.insurance.name}
          compact
        />
        {formattedPrice && (
          <Row
            label={t("page.schedules.newSchedule.confirm.price")}
            value={formattedPrice}
            compact
          />
        )}
        <CalendarAddress
          address={values.schedule.calendarAddress}
          compact
          showMapButton
        />
      </Stack>
      <Grid item xs={true}>
        <ConfirmDate
          calendarStatusCode={null}
          duration={values.schedule.minutesDuration}
          startHour={dayjs(values.schedule.calendarStartDate)}
          scheduleDate={values.schedule.calendarStartDate}
        />
      </Grid>
      <ConfirmPhoneNumber onGetSelectedCountry={() => {}} />
      <TasyOrientations
        rule={rule}
        moment="preConfirm"
        useNewScheduleInsuranceRegister={useNewScheduleInsuranceRegister}
      />
      <Antecipate />
    </Grid>
  );
};

export default memo(Confirm);
